import React from 'react'
import cx from 'classnames'
import Link from 'components/Link'
import styles from './Button.sass'

/**
 * Button component.
 */
const Button = (
  {
    borderless,
    children,
    className,
    dataTest,
    disabled,
    iconLeft,
    iconRight,
    loading,
    shadowless,
    submit,
    title,
    transparent,
    flat,
    to,
    replace,
    block,
    exact,
    href,
    onClick,
    type = 'default',
    size = 'normal',
    ...rest
  }: ButtonProps,
  ref: React.Ref<any>,
) => {
  const Element = submit ? 'button' : to ? (Link as any) : 'a'
  return (
    <Element
      ref={ref}
      disabled={disabled}
      title={title}
      data-test={dataTest}
      onClick={disabled ? undefined : onClick}
      {...(Element === Link
        ? { to, exact, replace }
        : Element === 'a'
          ? { href }
          : undefined)}
      className={cx(
        styles.button,
        styles[type] || styles.default,
        styles[size] || styles.normal,
        (iconLeft || iconRight) && styles.hasIcon,
        shadowless && styles.shadowless,
        block && styles.block,
        loading && styles.loading,
        borderless && styles.borderless,
        flat && styles.flat,
        disabled && styles.disabled,
        transparent && styles.transparent,
        !submit && styles.link,
        className,
      )}
      {...rest}
    >
      {iconLeft && <span className={styles.iconLeft}>{iconLeft}</span>}
      {children}
      {iconRight && <span className={styles.iconRight}>{iconRight}</span>}
    </Element>
  )
}

/**
 * Button props.
 */
export interface ButtonProps
  extends React.HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
  borderless?: boolean
  children?: React.ReactNode
  className?: string
  dataTest?: string
  disabled?: boolean
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  loading?: boolean
  shadowless?: boolean
  submit?: boolean
  title?: string
  transparent?: boolean
  type?: ButtonType
  size?: ButtonSize
  flat?: boolean
  block?: boolean
  to?: string
  replace?: boolean
  exact?: boolean
  href?: string
  target?: string
  form?: string
}

/**
 * Button size options.
 */
export type ButtonSize =
  | 'small'
  | 'normal'
  | 'large'
  | 'cta'
  | 'baseline'
  | 'wide'
  | 'wide-slim'

/**
 * Button types.
 */
export type ButtonType =
  | 'primary'
  | 'success'
  | 'danger'
  | 'secondary'
  | 'ternary'
  | 'default'
  | 'translucent'
  | 'text'
  | 'text-danger'

export default React.forwardRef(Button)
